import { useOneClickTradingV2 } from '@ui/features/OneClickTrading/hooks/useOneClickTradingV2';
import { useDataChainId } from '@ui/hooks/datachain';
import { useMutationToast } from '@ui/hooks/useMutationToast';
import { useSimulateMutation } from '@ui/hooks/useSimulateMutation';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { toBigValue } from '@ui/lib/bignumber';
import { pollV2IntentStatus } from '@ui/services/backend/oneClickV2Service';
import TradingPortalFacet from '@ui/services/contract/abis/TradingPortalFacet';
import { getContractAddress } from '@ui/services/contract/constants';
import { getTradingPortalFacet } from '@ui/services/contract/contract-instances/getTradingPortalFacet';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { PayablePoolToken } from '@ui/types/token';
import { TxnMutateCallBackParams } from '@ui/types/tx';
import { extractErrorMessage } from '@ui/utils/extractContractError';
import { increaseGasLimit } from '@ui/utils/increaseGastLimit';
import BigNumber from 'bignumber.js';
import { useMemo, useState } from 'react';
import { useAccount, usePublicClient } from 'wagmi';

import { RemoveMarginOracleData } from './useRemoveMarginOracleData';

export const useWithdrawCollateral = (opts?: TxnMutateCallBackParams) => {
  const { writeContract, isPending } = useTxnWriteContract(opts);
  const chainId = useDataChainId();
  const client = usePublicClient({
    chainId,
  });

  const { address: account } = useAccount();

  const { isSimulating, simulate } = useSimulateMutation();

  const mutationToastInfo = useMutationToast();
  const {
    isReady: canUseOneClickTradingV2,
    isSubmittingRemoveMargin: isSubmittingRemoveMarginV2,
    sendRemoveMargin: sendRemoveMarginV2,
  } = useOneClickTradingV2();
  const [isAwaitingOneClickResult, setIsAwaitingOneClickResult] = useState(false);

  return useMemo(() => {
    const addr = getContractAddress('LeverUp', chainId);
    if (!addr) {
      throw new Error('leverup contract address not found');
    }

    return {
      isPending: isSimulating || isPending || isSubmittingRemoveMarginV2 || isAwaitingOneClickResult,
      async writeContract(param: {
        tradeHash: Position['id'];
        payToken: PayablePoolToken;
        amount: BigNumber;
        oracleData: RemoveMarginOracleData;
      }) {
        const lvAmount = toBigValue(param.amount, param.payToken.decimals);

        // OneClick V2 path (priority) - only for non-native token removal (no oracle data needed)
        if (canUseOneClickTradingV2 && !param.payToken.isNative) {
          setIsAwaitingOneClickResult(true);
          mutationToastInfo.onMutate({
            title: 'In Progress',
            description: '⚡️ One-click V2 remove margin in progress',
          });

          try {
            const intentHash = await sendRemoveMarginV2(param.tradeHash, lvAmount);

            const status = await pollV2IntentStatus(intentHash);
            if (!status.success) {
              throw new Error(
                status.reason || 'One-click V2 remove margin execution failed',
              );
            }

            mutationToastInfo.onSuccess({
              title: '⚡️ Completed',
              description: 'Margin removed via one-click V2',
              txHash: status.txnHash as `0x${string}`,
            });

            return status.txnHash;
          } catch (error) {
            mutationToastInfo.onError({
              errorMsg:
                error instanceof Error
                  ? extractErrorMessage(error.message) ?? error.message
                  : undefined,
              onErrorToastOpts: {
                description: 'One-click V2 remove margin failed, please try again',
              },
            });
            throw error;
          } finally {
            mutationToastInfo.onSettled();
            setIsAwaitingOneClickResult(false);
          }
        }

        // On-chain path
        if (!client) return;

        const { oracleUpdateData, value } = param.oracleData;

        let estimateGasResult;

        const { isSuccess } = await simulate(async () => {
          const contract = getTradingPortalFacet({
            chainId,
            publicClient: client,
          });

          estimateGasResult = await contract.estimateGas.removeMargin(
            [param.tradeHash, lvAmount, oracleUpdateData],
            {
              account: account,
              value,
            },
          );
        });
        if (!isSuccess) {
          return;
        }

        const gasLimit = increaseGasLimit(estimateGasResult);

        return writeContract({
          abi: TradingPortalFacet,
          address: addr,
          functionName: 'removeMargin',
          args: [param.tradeHash, lvAmount, oracleUpdateData],
          value,
          gas: gasLimit,
        });
      },
    };
  }, [
    chainId,
    isSimulating,
    isPending,
    isSubmittingRemoveMarginV2,
    isAwaitingOneClickResult,
    client,
    simulate,
    account,
    writeContract,
    canUseOneClickTradingV2,
    mutationToastInfo,
    sendRemoveMarginV2,
  ]);
};
